import React from "react";
import { useTheme } from "../hooks/useTheme";
import wordmarkOnLight from "../assets/logo-wordmark-on-light.png";
import wordmarkOnDark from "../assets/logo-wordmark-on-dark.png";

// The wordmark, in the cut that is legible on the current theme.
//
// One component rather than the same three lines in ten files: the mark has a
// navy-ink version and a white-ink version, and picking the wrong one makes it
// disappear rather than merely look off. Every call site that duplicated the
// choice was a place that could get it wrong later.
//
// Picked from the resolved theme, not a <picture> media query, so it follows
// the in-app toggle as well as the OS. The inline script in index.html sets
// the theme before React mounts, so this is correct on the first paint.
function BrandWordmark({ width = "clamp(100px, 25vw, 160px)", style, alt = "Lineup CFB", shimmer = false }) {
  const { theme } = useTheme();
  const src = theme === "dark" ? wordmarkOnDark : wordmarkOnLight;

  if (!shimmer) {
    return (
      <img
        src={src}
        alt={alt}
        style={{ width, height: "auto", display: "block", ...style }}
      />
    );
  }

  // The loading sweep: a light band moved across the mark's own alpha shape,
  // so it reads as a gleam on the letters rather than a bar sliding over a
  // box. WebkitMask/mask reuse the same PNG so the sweep can never spill
  // outside the wordmark it's meant to highlight.
  return (
    <div style={{ position: "relative", width, ...style }}>
      <img src={src} alt={alt} style={{ width: "100%", height: "auto", display: "block" }} />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          WebkitMaskImage: `url(${src})`,
          WebkitMaskSize: "100% 100%",
          WebkitMaskRepeat: "no-repeat",
          maskImage: `url(${src})`,
          maskSize: "100% 100%",
          maskRepeat: "no-repeat",
          background: "linear-gradient(100deg, transparent 35%, rgba(255,255,255,0.95) 50%, transparent 65%)",
          backgroundSize: "300% 100%",
          animation: "brand-wordmark-shimmer 1.8s ease-in-out infinite",
          // "screen" always lightens regardless of the underlying hue.
          // overlay/soft-light scale with the base color's own luminance, so
          // the sweep read clearly over navy and white ink but all but
          // disappeared over the wordmark's saturated green, where the base
          // was already close to full brightness in its own channel.
          mixBlendMode: "screen",
          pointerEvents: "none",
        }}
      />
      <style>{`
        @keyframes brand-wordmark-shimmer {
          0% { background-position: 120% 0; }
          60%, 100% { background-position: -120% 0; }
        }
      `}</style>
    </div>
  );
}

export default BrandWordmark;
